import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SharedHeader, SharedFooter, PageHero, Breadcrumb } from "../../components/site/SharedLayout";
import { getPage } from "../../services/api";

/* ─────────────────────────── style tokens ─────────────────────────── */
const BG = "#f5f7fb";

/* ─────────────────────── prose content styles ─────────────────────── */
// These target the raw HTML coming from the WordPress editor.
// Scoped under .legal-content so they never leak to other pages.
const proseStyles = `
  .legal-content {
    font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
    font-size: 15px;
    line-height: 1.8;
    color: #333;
    max-width: 860px;
    margin: 0 auto;
  }
  .legal-content h1,
  .legal-content h2,
  .legal-content h3,
  .legal-content h4 {
    color: #1a3c6e;
    font-weight: 700;
    margin: 32px 0 12px;
    line-height: 1.3;
  }
  .legal-content h1 { font-size: 28px; }
  .legal-content h2 { font-size: 22px; }
  .legal-content h3 { font-size: 18px; }
  .legal-content h4 { font-size: 15px; text-transform: uppercase; letter-spacing: 0.05em; }
  .legal-content p  { margin: 0 0 16px; }
  .legal-content ul,
  .legal-content ol { padding-left: 24px; margin: 0 0 16px; }
  .legal-content li { margin-bottom: 6px; }
  .legal-content a  { color: #B16116; text-decoration: underline; }
  .legal-content a:hover { color: #d06b1a; }
  .legal-content strong { font-weight: 700; color: #1a3c6e; }
  .legal-content hr {
    border: none;
    border-top: 1px solid #e5ecf9;
    margin: 32px 0;
  }
  .legal-content table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    font-size: 14px;
  }
  .legal-content th,
  .legal-content td {
    border: 1px solid #e5ecf9;
    padding: 10px 14px;
    text-align: left;
  }
  .legal-content th {
    background: #1a3c6e;
    color: #fff;
    font-weight: 600;
  }
`;

/* ══════════════════════════ COMPONENT ══════════════════════════════════════ */
export function LegalPage() {
const location = useLocation();
const slug = location.pathname.replace("/", "");

  const [page, setPage]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    // Reset state whenever the slug changes (user navigates between legal pages)
    setPage(null);
    setLoading(true);
    setError(null);

    getPage(slug)
      .then(setPage)
      .catch(err => setError(err.message || "Failed to load page."))
      .finally(() => setLoading(false));
  }, [slug]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
        <SharedHeader />
        <PageHero label="" title="Loading…" />
        <SharedFooter />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !page) {
    return (
      <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
        <SharedHeader />
        <PageHero label="" title="Page Not Found" />
        <section style={{ background: BG, padding: "60px 20px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", color: "#c0392b", fontSize: 15 }}>
            {error || "The page you are looking for could not be found."}
          </div>
        </section>
        <SharedFooter />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
      {/* Inject scoped prose styles once */}
      <style>{proseStyles}</style>

      <SharedHeader />

      <PageHero
  label="Legal"
  title={page.title}
  bgImage={page.featured_image}
/>

      <Breadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: page.title, path: `/${slug}` },
        ]}
      />

      {/* ── Content ── */}
      <section style={{ background: BG, padding: "60px 20px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            className="legal-content"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}

export default LegalPage;
